import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import gradelyLogo from '../../assets/logo/gradely-logo.png';

// Animation keyframes
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const breathe = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
`;

// Component 1: Spinning Logo with Pulsing Background
export const SpinningGradelyLoader = ({ size = 60, showText = true, text = "Processing..." }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Pulsing background circle */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: size * 1.5,
                        height: size * 1.5,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        opacity: 0.1,
                        animation: `${pulse} 2s ease-in-out infinite`
                    }}
                />

                {/* Spinning logo */}
                <Box
                    component="img"
                    src={gradelyLogo}
                    alt="Gradely Logo"
                    sx={{
                        width: size,
                        height: size,
                        animation: `${spin} 3s linear infinite`,
                        zIndex: 1
                    }}
                />
            </Box>

            {showText && (
                <Typography
                    variant="body1"
                    sx={{
                        color: 'primary.main',
                        fontWeight: 'medium',
                        animation: `${breathe} 2s ease-in-out infinite`
                    }}
                >
                    {text}
                </Typography>
            )}
        </Box>
    );
};

// Component 2: Floating Logo with Ripple Effects
export const FloatingGradelyLoader = ({ size = 60, showText = true, text = "Loading..." }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: size * 2,
                    height: size * 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Ripple effect circles */}
                {[1, 2, 3].map((index) => (
                    <Box
                        key={index}
                        sx={{
                            position: 'absolute',
                            width: size * 1.2,
                            height: size * 1.2,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'primary.main',
                            animation: `${ripple} 3s ease-out infinite`,
                            animationDelay: `${index * 0.5}s`
                        }}
                    />
                ))}

                {/* Floating logo */}
                <Box
                    component="img"
                    src={gradelyLogo}
                    alt="Gradely Logo"
                    sx={{
                        width: size,
                        height: size,
                        animation: `${float} 3s ease-in-out infinite`,
                        zIndex: 1,
                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.1))'
                    }}
                />
            </Box>

            {showText && (
                <Typography
                    variant="body1"
                    sx={{
                        color: 'primary.main',
                        fontWeight: 'medium',
                        textAlign: 'center'
                    }}
                >
                    {text}
                </Typography>
            )}
        </Box>
    );
};

export default { SpinningGradelyLoader, FloatingGradelyLoader };